import { useRef } from "react";
import "./styles.css";

type Props = {
    value: string;
    onChange: (value: string) => void;
    length?: number;
    disabled?: boolean;
};

export default function OtpInput({ value, onChange, length = 6, disabled }: Props) {
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
    const digits = Array.from({ length }, (_, i) => value[i] ?? "");

    /**
     * Spreads a run of digits across the boxes from `start`, leaving the caret on the last one it
     * filled. A code reaches the field as one string in more ways than it reaches it one key at a
     * time — a paste, an SMS autofill, a keyboard suggestion — so every one of those ends up here
     * instead of each path having its own idea of what to do with the extra digits.
     */
    const fillFrom = (start: number, raw: string) => {
        const incoming = raw.replace(/\D/g, "");
        if (!incoming) return;

        // The value is a plain string and so cannot hold a gap: writing past the digits already
        // entered would collapse on join and print the code one box to the left of where it landed.
        const from = Math.min(start, value.length);

        const nextDigits = [...digits];
        for (let i = 0; i < incoming.length && from + i < length; i++) nextDigits[from + i] = incoming[i];
        onChange(nextDigits.join(""));

        inputsRef.current[Math.min(from + incoming.length, length - 1)]?.focus();
    };

    const handleChange = (index: number, raw: string) => {
        // Clearing a box is the one change that carries no digit, and it must leave the caret alone
        if (!raw) {
            const nextDigits = [...digits];
            nextDigits[index] = "";
            onChange(nextDigits.join(""));
            return;
        }
        fillFrom(index, raw);
    };

    const handlePaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
        // Taken over from the browser: `maxLength` clips the clipboard to one character on the way
        // in, so a pasted code would arrive here as its first digit and the rest would be dropped.
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
        // A whole code stays a whole code wherever it was dropped — pasting into the third box fills
        // the field from the start rather than throwing away the digits that precede it.
        fillFrom(pasted.length >= length ? 0 : index, pasted);
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && !digits[index] && index > 0) inputsRef.current[index - 1]?.focus();
    };

    return (
        <div className="otp-input">
            {digits.map((digit, index) => (
                <input
                    key={index}
                    ref={(el) => {
                        inputsRef.current[index] = el;
                    }}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    className={`otp-input-box${digit ? " otp-input-box--filled" : ""}`}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onPaste={(e) => handlePaste(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    disabled={disabled}
                />
            ))}
        </div>
    );
}
